import type { AxiosInstance } from "axios";
import api, { vaultActorApi, vaultPublicApi } from "./index";
import {
  DeleteFileResponse,
  Folder,
  Requirement,
  FormValue,
} from "../redux/interfaces/folder.types";
import {
  OnGenerateJWTResponse,
  OnUploadFileToVaultResponse,
} from "../redux/interfaces/vault.types";
import type {
  VerifyIdentityResponse,
  VerifyProofOfAddressResponse,
} from "../redux/actions/verificationActions";
import type { InitiateUploadInput } from "../utils/vault/resumableUpload";
import {
  defaultApiRetryDelay,
  retryTransientApiRequest,
} from "../utils/vault/resumableApiRecovery";
import {
  summarizeFormData,
  summarizeVerificationResponse,
  verificationDebugError,
  verificationDebugLog,
} from "../utils/verificationDebug";

const RESUMABLE_METADATA_TIMEOUT_MS = 30_000;

function patchUploadSubmission<T>(
  uploadBatchId: string | undefined,
  request: () => Promise<T>,
): Promise<T> {
  return uploadBatchId
    ? retryTransientApiRequest(request, defaultApiRetryDelay)
    : request();
}

const createVaultApi = (client: AxiosInstance) => ({
  // Folder endpoints
  getFolderById: (id: string) =>
    client.get<Folder>(`/docvault/folders/${id}`).then((r) => r.data),

  // Requirement endpoints
  getRequirementById: (id: string) =>
    client.get<Requirement>(`/docvault/requirements/${id}`).then((r) => r.data),

  updateFormValues: (
    reqId: string,
    body: {
      form_values: FormValue[] | null;
      status?: string;
      upload_batch_id?: string;
      upload_batch_action?: "replace_evidence" | "replace_evidence_on_submit";
    },
  ) => {
    const request = () =>
      body.upload_batch_id
        ? client.patch<Requirement>(`/docvault/requirements/${reqId}/`, body, {
            timeout: RESUMABLE_METADATA_TIMEOUT_MS,
          })
        : client.patch<Requirement>(`/docvault/requirements/${reqId}/`, body);
    return patchUploadSubmission(body.upload_batch_id, request).then(
      (response) => response.data,
    );
  },

  uploadFile: (reqId: string, formData: FormData) =>
    client
      .post<OnUploadFileToVaultResponse>(
        `/docvault/requirements/${reqId}/document-file/upload/`,
        formData,
      )
      .then((r) => r.data),

  initiateUpload: (reqId: string, input: InitiateUploadInput) =>
    client
      .post<unknown>(
        `/docvault/requirements/${reqId}/uploads/initiate/`,
        input,
        { timeout: RESUMABLE_METADATA_TIMEOUT_MS },
      )
      .then((response) => response.data),

  finalizeUpload: (reqId: string, uploadId: string) =>
    client
      .post<unknown>(
        `/docvault/requirements/${reqId}/uploads/${uploadId}/finalize/`,
        undefined,
        { timeout: RESUMABLE_METADATA_TIMEOUT_MS },
      )
      .then((response) => response.data),

  getUploadStatus: (reqId: string, uploadId: string) =>
    client
      .get<unknown>(`/docvault/requirements/${reqId}/uploads/${uploadId}/`, {
        timeout: RESUMABLE_METADATA_TIMEOUT_MS,
      })
      .then((response) => response.data),

  cancelUploadBatch: (reqId: string, batchId: string) =>
    client
      .post<unknown>(
        `/docvault/requirements/${reqId}/uploads/batches/${batchId}/cancel/`,
        undefined,
        { timeout: RESUMABLE_METADATA_TIMEOUT_MS },
      )
      .then((response) => response.data),

  confirmUploadBatch: (reqId: string, batchId: string) =>
    client
      .post<unknown>(
        `/docvault/requirements/${reqId}/uploads/batches/${batchId}/confirm/`,
        undefined,
        { timeout: RESUMABLE_METADATA_TIMEOUT_MS },
      )
      .then((response) => response.data),

  updateRequirementStatus: (
    reqId: string,
    status: string,
    uploadBatchId?: string,
    uploadBatchAction?: "replace_evidence_on_submit",
  ) => {
    const body = {
      status,
      ...(uploadBatchId ? { upload_batch_id: uploadBatchId } : {}),
      ...(uploadBatchAction ? { upload_batch_action: uploadBatchAction } : {}),
    };
    const request = () =>
      uploadBatchId
        ? client.patch<Requirement>(`/docvault/requirements/${reqId}/`, body, {
            timeout: RESUMABLE_METADATA_TIMEOUT_MS,
          })
        : client.patch<Requirement>(`/docvault/requirements/${reqId}/`, body);
    return patchUploadSubmission(uploadBatchId, request).then(
      (response) => response.data,
    );
  },

  cancelToReview: (reqId: string) =>
    client
      .patch<Requirement>(`/docvault/requirements/${reqId}/`, {
        status: "pending_upload",
      })
      .then((r) => r.data),

  updateWorkflowStep: (
    reqId: string,
    body: { workflow_step: string; confirmed_data?: Record<string, any> },
  ) =>
    client
      .patch<Requirement>(`/docvault/requirements/${reqId}/workflow-step`, body)
      .then((r) => r.data),

  validateProofOfAddressByIdentity: (poaId: string, identityReqId: string) =>
    client
      .post(`/docvault/requirements/${poaId}/validate-by-identity`, {
        identity_requirement_id: identityReqId,
      })
      .then((r) => r.data),

  verifyIdentity: async (
    formData: FormData,
  ): Promise<VerifyIdentityResponse> => {
    const requestSummary = summarizeFormData(formData);
    verificationDebugLog("vaultApi.verifyIdentity.request", {
      formData: requestSummary,
    });

    try {
      const response = await client.post<VerifyIdentityResponse>(
        `/docvault/documents/verify-identity`,
        formData,
        {
          timeout: 285000,
        },
      );
      verificationDebugLog(
        "vaultApi.verifyIdentity.response",
        {
          response: summarizeVerificationResponse(response.data),
        },
        response.data,
      );
      return response.data;
    } catch (error) {
      verificationDebugError("vaultApi.verifyIdentity.error", error, {
        formData: requestSummary,
      });
      throw error;
    }
  },

  verifyProofOfAddress: async (
    formData: FormData,
  ): Promise<VerifyProofOfAddressResponse> => {
    const requestSummary = summarizeFormData(formData);
    verificationDebugLog("vaultApi.verifyProofOfAddress.request", {
      formData: requestSummary,
    });

    try {
      const response = await client.post<VerifyProofOfAddressResponse>(
        `/docvault/documents/verify-proof-of-address`,
        formData,
        {
          timeout: 180000,
        },
      );
      verificationDebugLog(
        "vaultApi.verifyProofOfAddress.response",
        {
          response: summarizeVerificationResponse(response.data),
        },
        response.data,
      );
      return response.data;
    } catch (error) {
      verificationDebugError("vaultApi.verifyProofOfAddress.error", error, {
        formData: requestSummary,
      });
      throw error;
    }
  },

  removeFile: (fileId: string) =>
    client
      .delete<DeleteFileResponse>(
        `/docvault/requirements/document-file/${fileId}/delete`,
      )
      .then((r) => r.data),

  downloadFile: (fileId: string) =>
    client
      .get(`/docvault/requirements/document-file/${fileId}/download/`, {
        responseType: "blob",
      })
      .then((r) => r.data),
});

const actorVaultApi = createVaultApi(vaultActorApi);
export const staffVaultApi = createVaultApi(api);

export const vaultApi = {
  ...actorVaultApi,
  generateJWT: (folderId: string, email: string) =>
    vaultPublicApi
      .post<OnGenerateJWTResponse>(`/docvault/generate-jwt`, {
        folder_id: folderId,
        email,
      })
      .then((r) => r.data),
};

export function resolveScopedVaultApi(pathname: string) {
  return pathname.includes("vault") ? vaultApi : staffVaultApi;
}
